import { useLazyQuery } from '@apollo/client';
import { useDebounce } from '@bechtel/client-common';
import { GET_WORKLIST_SUMMARY_MILESTONES } from '@bechtel/client-gql';
import { Box, Checkbox, Icon, IconSize, Text } from '@bechtel/gds';
import {
  CommodityType,
  MilestoneStatusCode,
  MilestoneType,
  MilestoneTypeCount,
  OwnershipLevel,
  SortOrder,
  WeeklyWorkInput,
  WeeklyWorkListSearchBy,
  WeeklyWorkListSortByTerm,
  WeeklyWorkSummary,
} from '@bechtel/types';
import { formatISO, parseISO } from 'date-fns';
import {
  cloneDeep,
  concat,
  differenceBy,
  find,
  findIndex,
  forEach,
  get,
  map,
  reduce,
  some,
  sortBy,
  values,
} from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { CommodityGroupHeaderStyled, ContentStyled, ListSelectionStyled, MilestoneListStyled } from './styled';
import { CommodityMilestoneGroup, MilestoneFilterTypes } from './types';

export const MilestoneFilters: FC<MilestoneFilterTypes> = ({
  selectionStateValues,
  user,
  drawerValues,
  crewMember,
  searchQuery,
  onChange,
  weeklyWorkListItems,
}) => {
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const currentWeekStartDay = currentProjectWeek?.startDate;
  const currentWeekEndDay = currentProjectWeek?.endDate;

  const claimedFilter = selectionStateValues?.milestoneStatus === MilestoneStatusCode.CLAIMED ? true : false;
  const selectedWeekStartDay = selectionStateValues?.weekStartDay
    ? selectionStateValues?.weekStartDay
    : currentWeekStartDay;

  const selectedWeekEndDay = selectionStateValues?.weekEndDay ? selectionStateValues?.weekEndDay : currentWeekEndDay;
  const debouncedSearchQuery = useDebounce(searchQuery?.trim(), 500);
  const [loadWorklistSummary, { data }] = useLazyQuery<
    {
      weeklyWorkList: WeeklyWorkSummary;
    },
    {
      input: WeeklyWorkInput;
    }
  >(GET_WORKLIST_SUMMARY_MILESTONES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!crewMember?.id || !crewMember?.persona || !currentWeekStartDay || !selectedWeekEndDay) return;
    const weekStartDate = selectedWeekStartDay instanceof Date ? selectedWeekStartDay : parseISO(selectedWeekStartDay);
    const weekEndDate = selectedWeekEndDay instanceof Date ? selectedWeekEndDay : parseISO(selectedWeekEndDay);
    const worklistInput: WeeklyWorkInput = {
      crewMemberId: crewMember?.id,
      personaType: crewMember?.persona,
      startDate: formatISO(weekStartDate, { representation: 'date' }),
      endDate: formatISO(weekEndDate, { representation: 'date' }),
      searchValue: debouncedSearchQuery,
      searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
      ownershipLevel: OwnershipLevel.ALL,
      workPackageIds: [],
      filterByMilestoneTypes: map(
        drawerValues?.filterByMilestoneTypes,
        (milestoneType: MilestoneType) => milestoneType.id
      ),
      filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
      filterByMilestoneStatus: claimedFilter ? MilestoneStatusCode.CLAIMED : MilestoneStatusCode.PLANNED,
      filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
      sortBy: WeeklyWorkListSortByTerm.MILESTONE_TYPE,
      sortOrder: SortOrder.ASC,
    };

    loadWorklistSummary({
      variables: {
        input: { ...worklistInput, filterByMilestoneTypes: [] },
      },
    });
  }, [
    loadWorklistSummary,
    user,
    currentWeekStartDay,
    selectedWeekEndDay,
    selectedWeekStartDay,
    claimedFilter,
    drawerValues,
    debouncedSearchQuery,
    crewMember,
    weeklyWorkListItems,
  ]);

  const workSummary: WeeklyWorkSummary = get(data, 'weeklyWorkSummary');
  const { filterByMilestoneTypes } = drawerValues;
  const [milestoneFilters, setMilestoneFilters] = useState<MilestoneTypeCount[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!workSummary || !workSummary.milestoneCountsByType) return;
    const uniqueMilestoneCountsByType = reduce(
      workSummary.milestoneCountsByType,
      (milestoneList: any, milestoneCountsByTypeItem) => {
        if (!milestoneList[milestoneCountsByTypeItem.milestoneType.id]) {
          milestoneList[milestoneCountsByTypeItem.milestoneType.id] = cloneDeep(milestoneCountsByTypeItem);
        } else if (milestoneList[milestoneCountsByTypeItem.milestoneType.id]) {
          milestoneList[milestoneCountsByTypeItem.milestoneType.id].numMilestones +=
            milestoneCountsByTypeItem.numMilestones;
        }
        return milestoneList;
      },
      []
    );
    const milestoneCountsByType = values(uniqueMilestoneCountsByType);
    const selectedMilestonesNotinFilters = differenceBy(
      filterByMilestoneTypes,
      map(milestoneCountsByType, (mct) => mct.milestoneType),
      'id'
    );
    if (!selectedMilestonesNotinFilters) {
      setMilestoneFilters(milestoneCountsByType);
      return;
    }
    const mergedFilters = concat(
      milestoneCountsByType,
      map(selectedMilestonesNotinFilters, (selectedFilter) => {
        return { milestoneType: selectedFilter, numMilestones: 0 };
      })
    );
    setMilestoneFilters(mergedFilters);
  }, [filterByMilestoneTypes, workSummary]);

  const groupedByCommodityType = useMemo((): CommodityMilestoneGroup[] => {
    const groupMap: Record<string, CommodityMilestoneGroup> = {};
    const OTHER_KEY = '__other__';

    forEach(milestoneFilters, (milestoneCount) => {
      const commodityTypes = milestoneCount.milestoneType.commodityTypes;
      if (!commodityTypes || commodityTypes.length === 0) {
        if (!groupMap[OTHER_KEY]) {
          groupMap[OTHER_KEY] = { commodityType: { id: OTHER_KEY, name: 'Other' }, milestones: [] };
        }
        groupMap[OTHER_KEY].milestones.push(milestoneCount);
      } else {
        forEach(commodityTypes, (ct) => {
          if (!groupMap[ct.id]) {
            groupMap[ct.id] = { commodityType: { id: ct.id, name: ct.name }, milestones: [] };
          }
          groupMap[ct.id].milestones.push(milestoneCount);
        });
      }
    });

    return sortBy(values(groupMap), (group) => group.commodityType.name);
  }, [milestoneFilters]);

  const toggleGroup = (commodityTypeId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [commodityTypeId]: !prev[commodityTypeId] }));
  };

  return (
    <ContentStyled data-testid="milestone-filters">
      {groupedByCommodityType.length > 0 && (
        <MilestoneListStyled flexDirection={{ sm: 'column' }}>
          {groupedByCommodityType.map((group) => {
            const isExpanded = !!expandedGroups[group.commodityType.id];
            return (
              <Box key={group.commodityType.id} flexDirection={{ sm: 'column' }}>
                <CommodityGroupHeaderStyled
                  data-testid={`commodity-group-${group.commodityType.name}`}
                  onClick={() => toggleGroup(group.commodityType.id)}
                >
                  <Text color="grey.1000" size="300" weight="600">
                    {group.commodityType.name}
                  </Text>
                  <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} size={IconSize.SMALL} fillColor="grey.700" />
                </CommodityGroupHeaderStyled>
                {isExpanded &&
                  group.milestones.map((milestoneCount) => {
                    const { milestoneType, numMilestones } = milestoneCount;
                    return (
                      <ListSelectionStyled key={`${group.commodityType.id}-${milestoneType.id}`}>
                        <Box mr={{ sm: '500' }}>
                          <Checkbox
                            data-testid={`search-by-${milestoneType.name}`}
                            checked={some(drawerValues?.filterByMilestoneTypes, (filterByMilestoneType) => {
                              return filterByMilestoneType.id === milestoneType.id;
                            })}
                            onChange={() => {
                              const selected = findIndex(filterByMilestoneTypes, (filterByMilestoneType) => {
                                return filterByMilestoneType.id === milestoneType.id;
                              });
                              if (selected !== -1) {
                                filterByMilestoneTypes.splice(selected, 1);
                              } else {
                                filterByMilestoneTypes.push(milestoneType);
                              }
                              onChange(filterByMilestoneTypes);
                            }}
                            readOnly
                          />
                        </Box>
                        <Text color="grey.1000" size="400">
                          {milestoneType.name}
                          {` (${numMilestones})`}
                        </Text>
                      </ListSelectionStyled>
                    );
                  })}
              </Box>
            );
          })}
        </MilestoneListStyled>
      )}
    </ContentStyled>
  );
};
